/**
 * Moving, turning and resizing a model that is already in the room.
 *
 * Placement puts a model somewhere sensible to begin with; this is what happens when the
 * user disagrees. All of it is expressed relative to the viewer rather than in world axes,
 * because the thing being asked for is "bring that nearer", "swing it round to where I can
 * see it" and "show me its other side", none of which has anything to do with which way the
 * room's z axis happens to run.
 *
 * The room itself never moves. Walking the viewer about with a stick is the usual VR
 * convention and is deliberately not what happens here: several models standing at true
 * size are being compared against *each other*, and gliding the observer would be a far
 * larger change to the thing on screen than moving the one model that is in the way.
 *
 * Pure functions over plain numbers, like the rest of `domain` — no three, no gamepad, no
 * frame loop. What a stick is resting at, and how long it has been there, are handed in.
 */

import type { Quat, Vec3 } from './types.js';

/**
 * How far a stick is pushed, with any dead zone already taken out.
 *
 * `forward` is positive away from the user, which is the opposite of the raw axis the
 * hardware reports; flipping it belongs with the hardware, not here.
 */
export interface Stick {
  /** -1 fully left, 1 fully right. */
  readonly x: number;
  /** -1 fully back, 1 fully forward. */
  readonly forward: number;
}

/** How fast a model closes on or retreats from the viewer, in metres per second. */
export const PUSH_SPEED_M_PER_S = 0.8;

/** How fast a model swings round the viewer, in radians per second. */
export const SWING_RATE_RAD_PER_S = Math.PI / 4;

/**
 * Closest a model may be pushed to the viewer, in metres.
 *
 * Short of arm's length rather than zero: a model driven onto the same spot as the head
 * fills the view with the inside of itself, and there is no obvious stick movement that
 * gets you back out of it.
 */
export const MIN_DISTANCE_M = 0.25;

/** Furthest a model may be pushed, in metres. Past this it is a speck in a large room. */
export const MAX_DISTANCE_M = 8;

/** Doubling time at full deflection: one second to twice the size, or to half it. */
const GROWTH_RATE_PER_S = Math.LN2;

/** How far from true size the model may be taken, as a multiple of it. */
export const MIN_SCALE_RATIO = 0.05;
export const MAX_SCALE_RATIO = 20;

/**
 * How long the stick must be held against true size before the model will leave it.
 *
 * Long enough to be felt as the model stopping, short enough that pushing through it is
 * not a chore. Without a dwell the detent would last a single frame and amount to nothing.
 */
export const DETENT_DWELL_S = 0.35;

/**
 * Move a model nearer, further, or round the viewer, leaving its height alone.
 *
 * Swinging keeps the distance it is already at, so a model pushed across the room stays
 * across the room as it arcs. That is the useful gesture at range, where sliding sideways
 * by a fixed number of metres barely changes what you can see of the thing.
 *
 * Height is left out on purpose. A model's resting height is already decided by whether
 * it is small enough to float or large enough to stand on the floor, and a hand can lift
 * it directly; a third stick axis would be a way of accidentally leaving a model hanging
 * in mid-air.
 *
 * @param position where the model's origin is now, in metres.
 * @param viewer where the observer's head is, in metres. Only its horizontal part is used.
 * @param seconds since the previous frame.
 */
export function nudgePosition(
  position: Vec3,
  viewer: Vec3,
  stick: Stick,
  seconds: number,
): Vec3 {
  const [, height] = position;
  const offsetX = position[0] - viewer[0];
  const offsetZ = position[2] - viewer[2];

  // Directly above or below the viewer there is no line of sight to swing about and no
  // direction to push along, so there is nothing this can sensibly do. Only reachable by
  // hand — the distance clamp keeps the stick from ever arriving here.
  const distance = Math.hypot(offsetX, offsetZ);
  if (distance === 0) return position;

  // Negative, so that pushing the stick right moves the model right. What is being turned
  // is the line of sight from the viewer to the model, so it reads the same way whichever
  // direction the viewer happens to be facing.
  const angle = -stick.x * SWING_RATE_RAD_PER_S * seconds;
  const turnedX = offsetX * Math.cos(angle) + offsetZ * Math.sin(angle);
  const turnedZ = -offsetX * Math.sin(angle) + offsetZ * Math.cos(angle);

  const reach = clamp(
    distance + stick.forward * PUSH_SPEED_M_PER_S * seconds,
    MIN_DISTANCE_M,
    MAX_DISTANCE_M,
  );
  const stretch = reach / distance;

  return [viewer[0] + turnedX * stretch, height, viewer[2] + turnedZ * stretch];
}

/** How fast a model turns under the stick, in radians per second. */
export const TURN_RATE_RAD_PER_S = Math.PI / 2;

/** The room's vertical, which is the axis a model spins about however it is standing. */
const UP: Vec3 = [0, 1, 0];

const IDENTITY: Quat = [0, 0, 0, 1];

/**
 * Spin or tip a model where it stands, leaving where it stands alone.
 *
 * **The surface facing you follows the stick.** Push it right and the near face travels
 * right; push it away and the near face rides up over the top, which is the same thing as
 * the far side coming down towards you. One rule for both axes, and the one you would
 * predict from having put a hand on the thing and pushed.
 *
 * Spinning needs no reference to the viewer: turning a model anticlockwise seen from above
 * drags whichever face happens to be nearest towards the viewer's right, and that is true
 * from anywhere in the room, so the axis is simply the room's vertical. Tipping does need
 * one, since "away from you" names an axis that runs across the line of sight rather than
 * along any of the room's.
 *
 * Roll — turning the model about the line of sight — is deliberately not offered. It is the
 * least useful of the three for looking at a part, a stick has only two axes to spend, and
 * a hand can roll a model directly by taking hold of it.
 *
 * @param rotation the model's orientation now.
 * @param position where the model's origin is, in metres. Only its horizontal part is used.
 * @param viewer where the observer's head is, in metres. Likewise.
 * @param seconds since the previous frame.
 */
export function nudgeRotation(
  rotation: Quat,
  position: Vec3,
  viewer: Vec3,
  stick: Stick,
  seconds: number,
): Quat {
  if (stick.x === 0 && stick.forward === 0) return rotation;

  let delta = aboutAxis(UP, stick.x * TURN_RATE_RAD_PER_S * seconds);

  const offsetX = position[0] - viewer[0];
  const offsetZ = position[2] - viewer[2];
  const distance = Math.hypot(offsetX, offsetZ);

  // Directly above or below the viewer there is no line of sight to tip across, the same
  // degenerate case that leaves `nudgePosition` with nothing to do. Spinning still works,
  // since it never needed one.
  if (distance > 0) {
    // The viewer's right hand, horizontal: the room's vertical crossed with the direction
    // back towards the viewer. Negated `forward`, so that pushing the stick away sends the
    // top of the model away rather than bringing it towards you.
    const right: Vec3 = [-offsetZ / distance, 0, offsetX / distance];
    const tip = aboutAxis(right, -stick.forward * TURN_RATE_RAD_PER_S * seconds);
    delta = multiply(tip, delta);
  }

  // Applied to the outside of the model's existing orientation rather than composed into
  // it, so that the axes stay the room's and the viewer's however the model has already
  // been turned — otherwise a model spun half round would tip the wrong way.
  //
  // Renormalised because this runs sixty times a second for as long as the stick is held,
  // and a unit quaternion multiplied that many times does not stay one.
  return normalised(multiply(delta, rotation));
}

/** A turn of `angle` about `axis`, which must already be a unit vector. */
function aboutAxis(axis: Vec3, angle: number): Quat {
  const half = Math.sin(angle / 2);
  return [axis[0] * half, axis[1] * half, axis[2] * half, Math.cos(angle / 2)];
}

/** `a` then `b`, in the sense that the result turns a vector by `b` and then by `a`. */
function multiply(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;

  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

function normalised(q: Quat): Quat {
  const length = Math.hypot(...q);
  if (length === 0) return IDENTITY;
  return [q[0] / length, q[1] / length, q[2] / length, q[3] / length];
}

/** Where a resize got to, and what the stick has to do to carry on past true size. */
export interface ScaleNudge {
  /** The new scale, in the same terms as the old one. */
  readonly scale: number;
  /** Seconds the stick has now been held against the detent. Hand back next frame. */
  readonly dwell: number;
  /** True on the one step that lands exactly on true size. */
  readonly settled: boolean;
}

/**
 * Grow or shrink a model, stopping on the way past its true size.
 *
 * Growth is exponential, so a given push changes the size by a proportion rather than by
 * a number of millimetres — which is the only thing that behaves the same for a 2 mm screw
 * and a 300 mm bracket.
 *
 * The detent matters more here than it would in a modelling tool. This viewer's whole
 * claim is that what you are looking at is the size it says it is, so a resized model is
 * a model you can no longer measure against your hand, and getting back to the size that
 * can be trusted should not require reading a number off anything.
 *
 * @param scale the model's current scale factor.
 * @param trueScale the scale at which the model measures what the file says it does.
 * @param forward how hard the stick is pushed away from the user; positive grows.
 * @param dwell what the previous call returned, or 0 to start.
 */
export function nudgeScale(
  scale: number,
  trueScale: number,
  forward: number,
  seconds: number,
  dwell: number,
): ScaleNudge {
  if (forward === 0 || trueScale <= 0) return { scale, dwell: 0, settled: false };

  const ratio = scale / trueScale;

  if (ratio === 1) {
    const waited = dwell + seconds;
    // Still resting on it. The stick is being pushed and nothing is moving, which is what
    // a detent is.
    if (waited < DETENT_DWELL_S) return { scale, dwell: waited, settled: false };

    // Broken out of. The spent dwell is carried so that the model does not catch again on
    // the very next frame, having only just been released.
    return {
      scale: sized(1, forward, seconds, trueScale),
      dwell: waited,
      settled: false,
    };
  }

  const next = grow(ratio, forward, seconds);

  if ((ratio > 1 && next <= 1) || (ratio < 1 && next >= 1)) {
    return { scale: trueScale, dwell: 0, settled: true };
  }

  return { scale: sized(ratio, forward, seconds, trueScale), dwell, settled: false };
}

function grow(ratio: number, forward: number, seconds: number): number {
  return ratio * Math.exp(forward * GROWTH_RATE_PER_S * seconds);
}

function sized(
  ratio: number,
  forward: number,
  seconds: number,
  trueScale: number,
): number {
  return (
    trueScale * clamp(grow(ratio, forward, seconds), MIN_SCALE_RATIO, MAX_SCALE_RATIO)
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/**
 * The multiple of true size a model is shown at in the room, when nothing says otherwise.
 *
 * One, meaning the size the file says it is. Everything about this viewer's claim to be a
 * measuring instrument rests on that being the starting point.
 */
export const TRUE_SIZE = 1;

/**
 * How far off true size still counts as being on it, as a fraction of it.
 *
 * A scale does not survive a trip through a hand unchanged. Grabbing re-parents a model to
 * the controller and letting go re-parents it back, and both of those decompose a matrix
 * into a position, a rotation and a scale — arithmetic that is exact for about half of all
 * orientations and out by a part in 10^15 for the rest. Compared for equality, that says a
 * model which has merely been picked up and put down is no longer the size its file claims,
 * which is both untrue and the one thing this viewer is for.
 *
 * Far above that drift and far below anything a stick can ask for: a single frame at full
 * deflection changes the size by around one percent, so no deliberate resize can land here.
 */
const TRUE_SIZE_TOLERANCE = 1e-6;

/**
 * A multiple of true size, with one that is within a hair of it taken to be exactly on it.
 *
 * Snapping rather than merely reporting, because the figure is stored, shown as a
 * percentage and shared with other sessions. A model recorded at 99.9999999999999% would
 * be a model permanently marked as resized, by an amount no one could see or undo.
 */
export function settleRatio(ratio: number): number {
  return Math.abs(ratio - TRUE_SIZE) < TRUE_SIZE_TOLERANCE ? TRUE_SIZE : ratio;
}

/**
 * A stored resize, or nothing if the record does not hold one this version can use.
 *
 * Records written before models could be resized carry no such field, and a record is not
 * necessarily one this version of the code wrote — the same reasoning as `colourOf`, and
 * for the same reason: an unchecked number reaching a transform scales a model to
 * something invisible or to something that fills the room, neither of which looks like an
 * error, both of which are one.
 *
 * Outside the range the sticks can reach is refused rather than clamped. A value that far
 * out is not a resize this application produced, and honouring half of it would be
 * guessing at what it meant.
 *
 * Settled on the way in, so that a figure written before that was done — or by a session
 * that has yet to be updated — is read as the true size it was meant to be.
 */
export function normaliseRoomScale(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  if (value < MIN_SCALE_RATIO || value > MAX_SCALE_RATIO) return undefined;
  return settleRatio(value);
}

/** How much bigger or smaller than true size to show a model, defaulting to true size. */
export function roomScaleOf(meta: { readonly roomScale?: number }): number {
  return normaliseRoomScale(meta.roomScale) ?? TRUE_SIZE;
}
